import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { IAttendanceTimesheetRaw, ISolveAttendanceQuery } from "../models/Hrm";

export const exportAttendanceToExcel = (
  data: IAttendanceTimesheetRaw,
  date: ISolveAttendanceQuery,
) => {
  const attendances = data.attendances || [];
  const summary = data.summary;
  const fullname = data.employee?.fullname || "Nhân viên";
  const email = data.employee?.email || "";

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([]);
  const start = dayjs(date.startDate);
  const end = dayjs(date.endDate);
  const daysInMonth = end.date();

  XLSX.utils.sheet_add_aoa(
    ws,
    [
      ["", "", "", "", start.month() + 1],
      ["", "", "", "", start.year()],
      ["", "", "", "", "", "", "Báo cáo chuyên cần hàng tháng"],
      [
        "",
        "",
        "",
        "",
        "",
        "",
        `Từ: ${start.format("YYYY-MM-DD")} Đến: ${end.format("YYYY-MM-DD")}`,
      ],
    ],
    { origin: "A1" },
  );

  const mainHeader = [
    "STT",
    "",
    "Họ và Tên",
    "VP",
    "Email",
    "Ngày vào công ty",
    "",
  ];
  for (let i = 1; i <= daysInMonth; i++) {
    const currentDay = start.date(i);
    const dayOfWeek = currentDay.day();
    const dayStr = dayOfWeek === 0 ? "CN" : `T${dayOfWeek + 1}`;
    mainHeader.push(`(${i})\n${dayStr}`);
  }
  mainHeader.push(
    `Số ngày phép năm ${start.year()}`,
    "Số phép đã sử dụng",
    "Số phép sử dụng trong tháng",
    "Tổng ngày công tính lương",
    "Công giờ làm thêm (phút)",
    "",
    "",
    "",
    "Số phút đi muộn/ về sớm (Trừ vào lương)",
  );

  const subHeader = Array(mainHeader.length).fill("");
  const otIndex = mainHeader.indexOf("Công giờ làm thêm (phút)");
  subHeader[otIndex] = "Ngày làm việc";
  subHeader[otIndex + 1] = "Ngày T7, CN";
  subHeader[otIndex + 2] = "Ngày lễ, tết";
  subHeader[otIndex + 3] = "Làm đêm";

  XLSX.utils.sheet_add_aoa(ws, [mainHeader, subHeader], {
    origin: "A12",
  });

  if (!ws["!merges"]) ws["!merges"] = [];
  ws["!merges"].push({
    s: { r: 11, c: otIndex },
    e: { r: 11, c: otIndex + 3 },
  });
  for (let c = 0; c < mainHeader.length; c++) {
    if (c < otIndex || c > otIndex + 3) {
      ws["!merges"].push({ s: { r: 11, c: c }, e: { r: 12, c: c } });
    }
  }

  const checkInRow = [1, "", fullname, "", email, "", "Giờ vào"];
  const checkOutRow = [1, "", fullname, "", email, "", "Giờ ra"];
  const overtimeRow = [1, "", fullname, "", email, "", "Tăng ca"];
  const lateRow = [1, "", fullname, "", email, "", "Đi muộn"];
  const earlyRow = [1, "", fullname, "", email, "", " Về sớm"];
  const attendanceRow = [1, "", fullname, "", email, "", "Ngày công"];

  let totalWeekendMinutes = 0;
  for (let i = 1; i <= daysInMonth; i++) {
    const attendance = attendances.find((a) => dayjs(a.checkIn).date() === i);
    checkInRow.push(
      attendance?.checkIn ? dayjs(attendance.checkIn).format("HH:mm:ss") : "-",
    );
    checkOutRow.push(
      attendance?.checkOut
        ? dayjs(attendance.checkOut).format("HH:mm:ss")
        : "-",
    );
    overtimeRow.push(attendance?.overtimeHours || 0);
    lateRow.push(attendance?.lateMinutes || 0);
    earlyRow.push(attendance?.earlyLeaveMinutes || 0);

    let dailyAttendance = 0;
    if (attendance?.checkIn && attendance?.checkOut) {
      const checkInTime = dayjs(attendance.checkIn);
      if (checkInTime.hour() >= 12) {
        dailyAttendance = 0.5;
      } else {
        dailyAttendance = 1;
      }

      if (checkInTime.day() === 0 || checkInTime.day() === 6) {
        const diff = dayjs(attendance.checkOut).diff(checkInTime, "minute");
        totalWeekendMinutes += diff;
      }
    }
    attendanceRow.push(dailyAttendance);
  }

  const summaryValues = [
    "",
    "",
    summary?.leaveRequestsTotal || 0,
    summary?.attendancesTotal || 0,
    (summary?.overtimeHoursTotal || 0) * 60,
    totalWeekendMinutes,
    (summary?.holidayShifts || 0) * 60,
    (summary?.nightShifts || 0) * 60,
    (summary?.lateMinutesTotal || 0) + (summary?.earlyMinutesTotal || 0),
  ];
  const emptyPad = Array(summaryValues.length).fill("");
  checkInRow.push(...emptyPad);
  checkOutRow.push(...emptyPad);
  overtimeRow.push(...emptyPad);
  lateRow.push(...emptyPad);
  earlyRow.push(...emptyPad);
  attendanceRow.push(...summaryValues);

  XLSX.utils.sheet_add_aoa(
    ws,
    [checkInRow, checkOutRow, overtimeRow, lateRow, earlyRow, attendanceRow],
    {
      origin: "A14",
    },
  );

  ws["!cols"] = [
    { wch: 10 },
    { wch: 15 },
    { wch: 25 },
    { wch: 10 },
    { wch: 30 },
    { wch: 20 },
    { wch: 20 },
    ...Array(daysInMonth).fill({ wch: 12 }),
    ...Array(summaryValues.length).fill({ wch: 15 }),
  ];

  if (ws["!ref"]) {
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) continue;
        if (!ws[cellAddress].s) ws[cellAddress].s = {};
        if (R === 2 && C === 6) {
          ws[cellAddress].s = {
            ...ws[cellAddress].s,
            font: { bold: true, sz: 16, color: { rgb: "1677FF" } },
          };
        }

        if (R === 11 || R === 12) {
          let bgColor = "D9E1F2";
          if (C >= 7 && C < 7 + daysInMonth) {
            const dayDate = start.date(C - 6);
            if (dayDate.day() === 0 || dayDate.day() === 6) {
              bgColor = "FFD966";
            }
          }
          ws[cellAddress].s = {
            ...ws[cellAddress].s,
            fill: { fgColor: { rgb: bgColor } },
            font: { bold: true },
            alignment: {
              horizontal: "center",
              vertical: "center",
              wrapText: true,
            },
            border: {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            },
          };
        }

        if (R >= 13 && R <= 18 && C <= range.e.c) {
          ws[cellAddress].s.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
          if (C >= 7 && C < 7 + daysInMonth) {
            const dayDate = start.date(C - 6);
            if (dayDate.day() === 0 || dayDate.day() === 6) {
              ws[cellAddress].s.fill = { fgColor: { rgb: "FFF2CC" } };
            }
          }
        }
      }
    }
  }

  XLSX.utils.book_append_sheet(wb, ws, "Chấm công");
  XLSX.writeFile(
    wb,
    `cham-cong-${fullname}-${start.format("DD-MM-YYYY")}-${end.format(
      "DD-MM-YYYY",
    )}.xlsx`,
  );
};
